import { useEffect, useRef, useState } from 'react';
import { SEAT_NAMES, teamOfSeat, type Seat } from '../../game/match';
import { confirmDialog } from '../../components/Confirm';
import { Avatar, Name, Sheet, Spinner, Toggle, teamAvatarFill } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { toast } from '../../lib/toast';
import { useRoom, type ConnStatus, type RoomConnection } from '../../lib/room';
import { chime } from '../../lib/sound';
import { useTableVoice, voiceSupportedHere } from '../../lib/voiceChat';
import type { RoomInfo, RoomView } from '../../shared/room';
import { NotFound } from '../shared';
import { GameTable } from './GameTable';
import { LobbySeats, SeatChooser, SpinStage, inviteTable } from './Lobby';

/** /r/:code — checks the table exists before opening a live connection to it. */
export function OnlineRoom({ code }: { code: string }) {
  const [state, setState] = useState<'checking' | 'ok' | 'missing' | 'offline'>('checking');
  useEffect(() => {
    api<RoomInfo>(`/rooms/${encodeURIComponent(code)}`)
      .then(() => setState('ok'))
      .catch((err) => setState(err instanceof ApiError && err.status === 404 ? 'missing' : 'offline'));
  }, [code]);
  if (state === 'checking') return <Spinner label="Finding the table" />;
  if (state === 'missing') return <NotFound message="That table doesn't exist (or has been closed)." />;
  if (state === 'offline') return <NotFound message="Online play needs a connection. Try again when you're online." />;
  return <Room code={code} />;
}

function Room({ code }: { code: string }) {
  const { view, status, conn } = useRoom(code);
  const voice = useTableVoice(conn, view);
  const [seatsOpen, setSeatsOpen] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const lastSpin = useRef<number | null>(null);

  // A soft chime whenever someone else joins (or comes back) — not for the first look at the table.
  const activeKey = view ? view.seatInfo.map((s, i) => (s.claimed && s.online && i !== view.you ? '1' : '0')).join('') : null;
  const lastActive = useRef<string | null>(null);
  useEffect(() => {
    const before = lastActive.current;
    lastActive.current = activeKey;
    if (before === null || activeKey === null) return;
    if ([...activeKey].some((c, i) => c === '1' && before[i] === '0')) chime();
  }, [activeKey]);

  // A spin that arrives while we're watching animates the wheel; on reload we just show the result.
  useEffect(() => {
    if (!view) return;
    if (lastSpin.current !== null && view.spinId > lastSpin.current) setRevealing(true);
    lastSpin.current = view.spinId;
  }, [view?.spinId, view]);

  if (!view) return <Spinner label="Joining the table" />;

  const atTable = !(view.you === null && view.seatInfo.some((s) => !s.claimed)) && !(view.phase === 'lobby' || revealing);
  const stage =
    view.you === null && view.seatInfo.some((s) => !s.claimed) ? (
      <SeatChooser view={view} conn={conn} />
    ) : view.phase === 'lobby' || revealing ? (
      <div className="space-y-4">
        <LobbySeats view={view} />
        <SpinStage view={view} conn={conn} reveal={revealing} onDone={() => setRevealing(false)} />
      </div>
    ) : (
      <GameTable view={view} conn={conn} />
    );

  return (
    <main className={`mx-auto max-w-lg space-y-3 px-4 pb-16 ${atTable ? "land:max-w-none land:space-y-2 land:px-[max(1.25rem,env(safe-area-inset-left))] land:pb-2" : ""}`}>
      <header className="flex items-center gap-2 pt-[max(1rem,env(safe-area-inset-top))] land:pt-2">
        <button
          type="button"
          className="btn btn-ghost h-11 w-11 shrink-0 rounded-full p-0"
          aria-label="Leave table"
          onClick={async () => {
            // Only a seated player mid-session needs the warning; spectators just go.
            if (view.you !== null) {
              const ok = await confirmDialog({
                title: 'Leave the table?',
                icon: '🚪',
                body: 'Your seat and cards stay yours. Open the table link again any time to rejoin right where you left off.',
                confirmLabel: 'Leave',
                cancelLabel: 'Stay',
                tone: 'danger',
              });
              if (!ok) return;
            }
            voice.leave();
            navigate('/');
          }}
        >
          ←
        </button>
        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => void inviteTable(code)} aria-label={`Table ${code}. Invite players`}>
          <span className="eyebrow block truncate text-[10px] whitespace-nowrap text-cream/60">{view.variant === 'bandh' ? 'Bandh Rang' : 'Open Rang'}</span>
          <span className="block truncate font-mono text-lg font-bold tracking-[0.14em] whitespace-nowrap">{code}</span>
        </button>
        <LinkBadge status={status} />
        {atTable && <RotateButton />}
        <VoiceControls view={view} voice={voice} />
        <button type="button" className="btn btn-ghost h-11 w-11 rounded-full p-0" onClick={() => setSeatsOpen(true)} aria-label="Players and seats">
          ⋯
        </button>
      </header>


      {stage}

      <SeatsSheet open={seatsOpen} onClose={() => setSeatsOpen(false)} view={view} conn={conn} />
    </main>
  );
}

type LockableOrientation = ScreenOrientation & { lock?: (o: 'landscape' | 'portrait') => Promise<void>; unlock?: () => void };

/**
 * Play sideways. Browsers only let a page rotate the screen in full screen (Android Chrome);
 * where that isn't possible (iPhone), tell the player to turn the phone themselves.
 */
function RotateButton() {
  const [sideways, setSideways] = useState(() => window.matchMedia('(orientation: landscape)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const sync = () => setSideways(mq.matches);
    mq.addEventListener('change', sync);
    // Leaving full screen (e.g. the phone's back gesture) also releases the lock.
    const onFs = () => !document.fullscreenElement && (screen.orientation as LockableOrientation).unlock?.();
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      mq.removeEventListener('change', sync);
      document.removeEventListener('fullscreenchange', onFs);
    };
  }, []);

  const toggle = async () => {
    const o = screen.orientation as LockableOrientation | undefined;
    try {
      if (!sideways) {
        if (!o?.lock || !document.documentElement.requestFullscreen) throw new Error('unsupported');
        if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
        await o.lock('landscape');
      } else {
        o?.unlock?.();
        if (document.fullscreenElement) await document.exitFullscreen();
      }
    } catch {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
      toast(sideways ? 'Turn your phone upright to go back.' : 'Turn your phone sideways (with Rotation Lock off) to play in landscape.', 'info', 4500);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/8 text-cream ring-1 ring-white/20 hover:bg-white/15"
      aria-label={sideways ? 'Back to portrait' : 'Play in landscape'}
      title={sideways ? 'Back to portrait' : 'Play in landscape'}
    >
      <svg viewBox="0 0 24 24" className={`h-5 w-5 transition-transform ${sideways ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="7" y="3" width="10" height="18" rx="2.2" />
        <path d="M11 18h2" />
        <path d="M20.5 9.5a8 8 0 0 0-3-5" />
        <path d="M17.5 2.5v2h2" />
      </svg>
    </button>
  );
}

function LinkBadge({ status }: { status: ConnStatus }) {
  if (status === 'open') return null;
  return (
    <span className="rounded-full bg-amber-200 px-2.5 py-1 text-xs font-semibold text-amber-950" aria-live="polite">
      {status === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
    </span>
  );
}

function VoiceControls({ view, voice }: { view: RoomView; voice: ReturnType<typeof useTableVoice> }) {
  if (!voiceSupportedHere() || view.you === null) return null;
  if (!voice.joined) {
    return (
      <button
        type="button"
        className="btn btn-ghost h-11 rounded-full px-3 text-sm"
        onClick={() => void voice.join()}
        disabled={voice.starting}
        aria-label="Join the table's voice chat"
      >
        🎙 {voice.starting ? '…' : 'Talk'}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <button
        type="button"
        onClick={voice.toggleMute}
        aria-pressed={voice.muted}
        aria-label={voice.muted ? 'Unmute your microphone' : 'Mute your microphone'}
        className={`grid h-11 w-11 place-items-center rounded-full text-lg ring-1 ${voice.muted ? 'bg-suit-red/80 ring-white/20' : 'bg-success-600 ring-white/30'}`}
      >
        {voice.muted ? '🔇' : '🎙'}
      </button>
      <button type="button" onClick={voice.leave} className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-xs" aria-label="Leave voice chat">
        ✕
      </button>
    </span>
  );
}

/** Who's where, who's online, and the host's "free this seat" for a player switching phones. */
function SeatsSheet({ open, onClose, view, conn }: { open: boolean; onClose: () => void; view: RoomView; conn: RoomConnection }) {
  return (
    <Sheet open={open} onClose={onClose} title="Players">
      <ul className="divide-y divide-ink/10">
        {view.seats.map((p, i) => {
          const seat = i as Seat;
          const info = view.seatInfo[seat]!;
          const team = view.teams[teamOfSeat(seat)];
          const mine = view.you === seat;
          return (
            <li key={seat} className="flex items-center gap-3 py-2.5">
              <Avatar name={p.name} avatarKey={p.avatarKey} size={40} ring={team.color} fill={teamAvatarFill(team.color)} />
              <span className="min-w-0 flex-1">
                <Name className="block truncate font-semibold">{p.name}</Name>
                <span className="text-xs text-ink-soft">
                  {team.name} · {SEAT_NAMES[seat]} · {mine ? 'you' : info.claimed ? (info.online ? 'online' : 'away') : 'not joined'}
                  {info.voice ? (info.muted ? ' · 🔇' : ' · 🎙 talking') : ''}
                </span>
              </span>
              {info.claimed && (view.isHost || (mine && view.phase === 'lobby')) && (
                <button
                  type="button"
                  className="btn btn-outline-ink min-h-9 px-3 text-xs"
                  onClick={() => conn.send({ t: 'release', seat })}
                  title="Lets this player rejoin from another phone"
                >
                  {mine ? 'Leave seat' : 'Free seat'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <button type="button" className="btn btn-ink mt-4 w-full" onClick={() => void inviteTable(view.code)}>
        🔗 Share table link
      </button>
      <div className="mt-4 rounded-xl bg-ink/5 px-3 py-2">
        <Toggle
          label="We're sitting together"
          hint={
            view.sameRoom
              ? `Only ${view.speaker !== null ? view.seats[view.speaker]!.name : 'one'}'s phone reads the announcements, so they don't play four times over.`
              : 'Turn on when all four phones are in the same room, so announcements play on just one.'
          }
          checked={view.sameRoom}
          disabled={!view.isHost}
          onChange={(on) => conn.send({ t: 'sameRoom', on })}
        />
      </div>
      {view.isHost && <p className="mt-2 text-center text-xs text-ink-soft">You opened this table, so you can free a seat if someone switches phones.</p>}
    </Sheet>
  );
}
